import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCircleAlert, lucideMail, lucideRefreshCw } from '@ng-icons/lucide';

import { TurnstileComponent } from '../../components/turnstile/turnstile.component';
import { UI } from '../../components/ui';
import { AuthService } from '../../services/auth.service';

/**
 * Password reset by email code: ask for a code, validate it, then land on the change-password page
 * (the code opens a restricted session that can only set a new password).
 */
@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [FormsModule, RouterLink, NgIcon, TurnstileComponent, ...UI],
  viewProviders: [provideIcons({ lucideCircleAlert, lucideMail, lucideRefreshCw })],
  template: `
    <div class="relative z-10 w-full max-w-105 rounded-2xl border border-border bg-card p-10 shadow-2xl">
      <div class="mb-6 flex flex-col items-center">
        <div class="flex items-center gap-3">
          <img src="main_money_square.png" alt="MainMoney" class="size-12 rounded-md object-contain" />
          <span class="text-[1.75rem] font-bold tracking-tight text-fg">Main Money</span>
        </div>
        <span class="mt-1 text-xs tracking-[0.3em] text-muted uppercase">Aggregator</span>
      </div>

      <h1 class="m-0 mb-2 text-center text-xl font-bold text-fg">Reset your password</h1>
      <p class="m-0 mb-8 text-center text-sm text-muted">
        @if (step() === 'email') {
          Enter your account email and we will send you a verification code.
        } @else {
          Enter the 6-digit code we sent to {{ maskedDestination() || 'your email' }}.
        }
      </p>

      @if (error()) {
        <ui-alert variant="danger" class="mb-4!">
          <div class="flex items-center gap-2">
            <ng-icon name="lucideCircleAlert" size="18" />
            <span>{{ error() }}</span>
          </div>
        </ui-alert>
      }

      @if (step() === 'email') {
        <form (ngSubmit)="requestCode()" class="flex flex-col gap-5">
          <label class="flex flex-col gap-2">
            <span class="text-sm font-semibold text-fg">Email</span>
            <div class="group relative flex items-center">
              <ng-icon name="lucideMail" size="18" class="pointer-events-none absolute left-4 text-muted transition-colors group-focus-within:text-fg" />
              <input uiInput class="py-3.5! pr-4! pl-11! text-base!" type="email" [(ngModel)]="email" name="email" autocomplete="email" placeholder="you@company.com" />
            </div>
          </label>
          <app-turnstile #emailTurnstile [(token)]="turnstileToken" />
          <button type="submit" uiButton variant="primary" size="lg" class="mt-2 font-bold" [disabled]="loading() || !turnstileToken">
            @if (loading()) {
              <ng-icon name="lucideRefreshCw" size="18" class="animate-spin" />
              <span>Sending…</span>
            } @else {
              <span>Send code</span>
            }
          </button>
        </form>
      } @else {
        <form (ngSubmit)="verifyCode()" class="flex flex-col gap-5">
          <ui-otp-input
            [(code)]="code"
            [autoFocus]="true"
            [invalid]="!!error()"
            [disabled]="loading()"
            label="Verification code"
            (completed)="verifyCode()"
          />
          <app-turnstile #codeTurnstile [(token)]="turnstileToken" />
          <button type="submit" uiButton variant="primary" size="lg" class="mt-2 font-bold" [disabled]="loading() || code.length < 6 || !turnstileToken">
            @if (loading()) {
              <ng-icon name="lucideRefreshCw" size="18" class="animate-spin" />
              <span>Verifying…</span>
            } @else {
              <span>Verify code</span>
            }
          </button>
          <button type="button" uiButton variant="outline" [disabled]="loading()" (click)="backToEmail()">Use a different email</button>
        </form>
      }

      <div class="mt-7 border-t border-border pt-5 text-center text-sm">
        <a routerLink="/login" class="font-semibold text-info-fg no-underline hover:underline">&larr; Back to sign in</a>
      </div>
    </div>
  `,
})
export class ForgotPasswordComponent {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  readonly step = signal<'email' | 'code'>('email');
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly maskedDestination = signal<string | null>(null);
  private otpId: string | null = null;

  email = '';
  code = '';
  turnstileToken = '';
  private readonly emailTurnstile = viewChild<TurnstileComponent>('emailTurnstile');
  private readonly codeTurnstile = viewChild<TurnstileComponent>('codeTurnstile');

  requestCode(): void {
    this.error.set(null);
    if (!this.email.trim()) {
      this.error.set('Please enter your email.');
      return;
    }
    this.loading.set(true);
    this.auth.requestPasswordReset(this.email.trim(), this.turnstileToken).subscribe({
      next: (res) => {
        this.loading.set(false);
        this.otpId = res.otp_id;
        this.maskedDestination.set(res.masked_destination);
        this.turnstileToken = '';
        this.code = '';
        this.step.set('code');
      },
      error: (err) => {
        this.loading.set(false);
        this.emailTurnstile()?.reset();
        this.error.set(this.message(err, 'Could not send the code. Please try again.'));
      },
    });
  }

  verifyCode(): void {
    if (this.loading() || this.code.length < 6) {
      return;
    }
    this.error.set(null);
    if (!this.otpId) {
      // The server answers the same way for unknown accounts: nothing was sent.
      this.error.set('Invalid or expired code.');
      return;
    }
    this.loading.set(true);
    this.auth.verifyPasswordReset(this.otpId, this.code, this.turnstileToken).subscribe({
      next: () => {
        this.loading.set(false);
        this.router.navigate(['/change-password']);
      },
      error: (err) => {
        this.loading.set(false);
        this.code = '';
        this.turnstileToken = '';
        this.codeTurnstile()?.reset();
        this.error.set(this.message(err, 'Invalid or expired code.'));
      },
    });
  }

  backToEmail(): void {
    this.error.set(null);
    this.turnstileToken = '';
    this.step.set('email');
  }

  private message(err: unknown, fallback: string): string {
    if (err instanceof HttpErrorResponse) {
      const detail = err.error?.detail;
      if (typeof detail === 'string' && detail.trim()) {
        return detail;
      }
    }
    return fallback;
  }
}
